import * as React from "react";
import { createContext, Dispatch, FC, SetStateAction, useContext, useState } from "react";
import { FilterOptions, IFilterOption } from "../Map/filters";
import { Filter, Option } from "../Map/enums";

export interface IDemoContext {
    placeSubtypeFilter: IFilterOption[];
    setPlaceSubtypeFilter: Dispatch<SetStateAction<IFilterOption[]>>;
    visitedFilter: IFilterOption[];
    setVisitedFilter: Dispatch<SetStateAction<IFilterOption[]>>;
    likeFilter: IFilterOption[];
    setLikeFilter: Dispatch<SetStateAction<IFilterOption[]>>;
    routeFilter: IFilterOption[];
    setRouteFilter: Dispatch<SetStateAction<IFilterOption[]>>;
    tourismTypeFilter: IFilterOption[];
    setTourismTypeFilter: Dispatch<SetStateAction<IFilterOption[]>>;
    nearbyFilter: boolean;
    setNearbyFilter: Dispatch<SetStateAction<boolean>>;
    lvRegionFilter: IFilterOption[];
    setLvRegionFilter: Dispatch<SetStateAction<IFilterOption[]>>;
    searchFieldList: any[];
    setSearchFieldList: Dispatch<SetStateAction<any[]>>;
    filterValues: FilterOptions;
    onFilterItemToggle: (
        filterName: string,
        value: string | number | boolean,
        event: any,
        onNearbyChange?: CallableFunction
    ) => void;
}

const DemoContext = createContext<IDemoContext>(null);

export const useDemoContext = (): IDemoContext => useContext(DemoContext);

export const DemoContextProvider: FC = (props: React.PropsWithChildren<IDemoContext>) => {
    const [placeSubtypeFilter, setPlaceSubtypeFilter] = useState<IFilterOption[]>([]);
    const [visitedFilter, setVisitedFilter] = useState<IFilterOption[]>([]);
    const [likeFilter, setLikeFilter] = useState<IFilterOption[]>([]);
    const [routeFilter, setRouteFilter] = useState<IFilterOption[]>([]);
    const [tourismTypeFilter, setTourismTypeFilter] = useState<IFilterOption[]>([]);
    const [nearbyFilter, setNearbyFilter] = useState(false);
    const [lvRegionFilter, setLvRegionFilter] = useState<IFilterOption[]>([]);
    const [searchFieldList, setSearchFieldList] = useState([]);

    const filterValues: FilterOptions = {
        [Filter.PLACESUBTYPE]: placeSubtypeFilter,
        [Filter.VISITED]: visitedFilter,
        [Filter.LIKE]: likeFilter,
        [Filter.ROUTE]: routeFilter,
        [Filter.TOURISMTYPESSTR]: tourismTypeFilter,
        [Filter.LVREGION]: lvRegionFilter,
        [Filter.nearby]: nearbyFilter
    };

    const onFilterItemToggle = (
        filterName: string,
        value: string | number | boolean,
        event: any,
        onNearbyChange?: CallableFunction
    ) => {
        if (filterName === Filter.PLACESUBTYPE) {
            setFilter(placeSubtypeFilter, setPlaceSubtypeFilter, value, event);
        }
        if (filterName === Filter.VISITED) {
            setFilter(visitedFilter, setVisitedFilter, value, event);
        }
        if (filterName === Filter.LIKE) {
            setFilter(likeFilter, setLikeFilter, value, event);
        }
        if (filterName === Filter.ROUTE) {
            setFilter(routeFilter, setRouteFilter, value, event);
        }
        if (filterName === Filter.TOURISMTYPESSTR) {
            setFilter(tourismTypeFilter, setTourismTypeFilter, value, event);
        }
        if (filterName === Filter.LVREGION) {
            setFilter(lvRegionFilter, setLvRegionFilter, value, event);
        }
        if (filterName === Filter.nearby) {
            setNearbyFilter(event.target.value === Option.IS ? true : false);
            onNearbyChange(event.target.value === Option.IS ? true : false);
        }
    };

    const setFilter = (
        filter: IFilterOption[],
        setFilterState: Dispatch<SetStateAction<IFilterOption[]>>,
        value: string | number | boolean,
        event: any
    ) => {
        const option = event.target.value;
        let updated: IFilterOption[];
        if (option === Option.IS) {
            const cleanBeforeAdd = filter.filter((f) => f.value !== value);
            updated = [...cleanBeforeAdd, { value, not: false }];
        } else if (option === Option.NOT) {
            const cleanBeforeAdd = filter.filter((f) => f.value !== value);
            updated = [...cleanBeforeAdd, { value, not: true }];
        } else {
            updated = filter.filter((f) => f.value !== value);
        }
        setFilterState(updated);
    };

    const values = {
        placeSubtypeFilter,
        setPlaceSubtypeFilter,
        visitedFilter,
        setVisitedFilter,
        likeFilter,
        setLikeFilter,
        routeFilter,
        setRouteFilter,
        tourismTypeFilter,
        setTourismTypeFilter,
        nearbyFilter,
        setNearbyFilter,
        lvRegionFilter,
        setLvRegionFilter,
        searchFieldList,
        setSearchFieldList,
        filterValues,
        onFilterItemToggle
    };

    return <DemoContext.Provider value={values}>{props.children}</DemoContext.Provider>;
};
